// src/features/services/ServiceWorkspacePage.tsx
import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { LoadingBoundary, PageContainer, PageProgress } from '../../app/components/foundation';
import {
  ServiceWorkspaceHeader,
  ServiceInfoCard,
} from './components';
import { useServiceWorkspace } from './hooks/useServiceWorkspace';
import type { ServiceStatus, ServiceType } from './types/service.types';
import './styles/services.css';

/**
 * Services Workspace Foundation (/services/:id). Same shell pattern
 * as CustomerWorkspacePage: PageContainer + feature-owned header,
 * card grid below, RTL/Persian throughout, existing tokens/primitives
 * only.
 *
 * Reads `id` from the route params and resolves the real Service
 * detail through the feature API hook. Only fields returned by the
 * backend are rendered.
 */
export function ServiceWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const {
    service,
    loading,
    error,
    actionLoading,
    actionError,
    actionSuccess,
    update,
    archive,
  } = useServiceWorkspace(id);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<ServiceType>('vpn');
  const [status, setStatus] = useState<ServiceStatus>('active');
  const [description, setDescription] = useState('');
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  useEffect(() => {
    if (!service) return;
    setName(service.name);
    setSlug(service.slug);
    setType(service.type);
    setStatus(service.status);
    setDescription(service.description ?? '');
  }, [service]);

  if (loading && !service) {
    return <PageContainer><LoadingBoundary loading /></PageContainer>;
  }

  if (error) {
    return <PageContainer><LoadingBoundary loading={false} error={error} /></PageContainer>;
  }

  if (!service) {
    return (
      <PageContainer className="flex flex-col gap-6">
        <p className="vania-service-workspace-empty">سرویس مورد نظر یافت نشد.</p>
      </PageContainer>
    );
  }

  const currentService = service;

  async function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await update({
      tenant_id: currentService.tenant_id,
      name,
      slug,
      type,
      status,
      description: description || null,
    });
  }

  async function handleArchive() {
    await archive(currentService.tenant_id);
    setArchiveConfirmOpen(false);
  }

  return (
    <PageContainer className="flex flex-col gap-6">
      <PageProgress active={loading} />
      <ServiceWorkspaceHeader service={service} />

      <div className="vania-service-workspace-grid">
        <ServiceInfoCard service={service} />
        <form className="vania-service-workspace-card vania-service-edit-form" onSubmit={handleUpdate}>
          <h2 className="vania-service-workspace-card-title">ویرایش سرویس</h2>
          <label>
            نام سرویس
            <input value={name} onChange={(event) => setName(event.target.value)} disabled={actionLoading} required />
          </label>
          <label>
            شناسه یکتا
            <input value={slug} onChange={(event) => setSlug(event.target.value)} disabled={actionLoading} required dir="ltr" />
          </label>
          <label>
            نوع سرویس
            <select value={type} onChange={(event) => setType(event.target.value as ServiceType)} disabled={actionLoading}>
              <option value="vpn">VPN</option>
              <option value="gold">Gold</option>
              <option value="saas">SaaS</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label>
            وضعیت
            <select value={status} onChange={(event) => setStatus(event.target.value as ServiceStatus)} disabled={actionLoading}>
              <option value="active">فعال</option>
              <option value="inactive">غیرفعال</option>
              <option value="archived">بایگانی شده</option>
            </select>
          </label>
          <label>
            توضیحات
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} disabled={actionLoading} rows={4} />
          </label>
          {actionError && <p className="vania-service-action-error">{actionError}</p>}
          {actionSuccess && <p className="vania-service-action-success">{actionSuccess}</p>}
          <div className="vania-service-workspace-actions">
            <button type="submit" className="vania-service-primary-action" disabled={actionLoading}>
              {actionLoading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
            </button>
            <button type="button" className="vania-service-danger-action" onClick={() => setArchiveConfirmOpen(true)} disabled={actionLoading || service.status === 'archived'}>
              بایگانی سرویس
            </button>
          </div>
          {archiveConfirmOpen && (
            <div className="vania-service-confirmation">
              <p>آیا از بایگانی این سرویس مطمئن هستید؟</p>
              <div className="vania-service-workspace-actions">
                <button type="button" className="vania-service-secondary-action" onClick={() => setArchiveConfirmOpen(false)} disabled={actionLoading}>انصراف</button>
                <button type="button" className="vania-service-danger-action" onClick={() => void handleArchive()} disabled={actionLoading}>تأیید بایگانی</button>
              </div>
            </div>
          )}
        </form>
        <div className="vania-service-workspace-card">
          <h2 className="vania-service-workspace-card-title">فراداده</h2>
          <pre className="vania-service-metadata" dir="ltr">
            {Object.keys(service.metadata).length > 0
              ? JSON.stringify(service.metadata, null, 2)
              : '—'}
          </pre>
        </div>
      </div>
    </PageContainer>
  );
}
